import WidgetHeader from '@/components/common/WidgetHeader';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { GRID_COLUMN_WIDTH_NARROW } from '@/constants/dimensions';
import { GRID_KEYS } from '@/constants/gridKeys';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useGridColumns } from '@/hooks/useGridColumns';

import {
  useGetHoldingRealizedGainsQuery,
  useGetRealizedGainsQuery,
  type PortfolioRealizedGainsQueryParams,
  type RealizedGainsQueryParams,
  type RealizedGainsRecord,
} from '@/store/holdingsApi';
import type { RecordResponse } from '@/models/ServiceTypes';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate } from '@/utils/date';
import {
  Box,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  GridCellParams,
  GridColDef,
  GridApiPremium,
  GridGroupNode,
  GridListViewColDef,
  GridRenderCellParams,
  GridRowId,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
  isAutogeneratedRow,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { Capacitor } from '@capacitor/core';
import {
  GainLossGroupByMode,
  getGainLossFilterQueryParams,
  getGainLossGroupingModel,
  formatTaxLotNumber,
  getHoldingRowDateFields,
  getTaxLotNumberSortComparator,
  shouldShowTaxLotDetailPanel,
  withTaxLotNumberColumn,
} from '@/modules/portfolio/gainLoss/constants';
import { useTickerUrlFormatter } from '@/hooks/useTickerUrlFormatter';
import useAccountDetailsFlyout from '@/hooks/useAccountDetailsFlyout';
import useHoldingDetailsFlyout from '@/hooks/useHoldingDetailsFlyout';
import { TaxLotsDetailGrid } from './TaxLotsDetailGrid';
import { ExportButton } from '../../../components/grids/ExportButton';
import { realizedGainsPercentChangeAggregation } from './aggregators/realizedGainsPercentChangeAggregation';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { formatPercentValue } from '@/utils/numbers';
import GainsLossesUtility from './utilities/gains-losses.utility';
import { GridContainer } from '../../../components/grids/GridContainer';

export interface RealizedGainLossTabProps {
  startDate?: string;
  endDate?: string;
  holdingId?: number;
  accountId?: number;
  isWidget?: boolean;
  investorId?: number;
  groupBy?: GainLossGroupByMode;
}

/** Stable empty Set for detail panel expanded IDs (same pattern as EMPTY_* in selectors: avoid allocations, stable reference). */
const EMPTY_EXPANDED_ROW_IDS = new Set<GridRowId>();
const REALIZED_LIST_VIEW_OPTIONAL_FIELDS = [
  'OpenDate',
  'TradeDate',
  'SaleDate',
  'TaxLotNumber',
  'SaleType',
  'Symbol',
  'CUSIP',
  'DiversificationName',
  'Quantity',
] as const;

/**
 * The grid row shape produced from a {@link RealizedGainsRecord}. Declared
 * explicitly so the columns and the mobile list-view read typed fields instead
 * of `any` (the RTK query data is loosely typed at this call site).
 */
export interface RealizedGainLossRow {
  id: number;
  WealthHoldingId: number;
  WealthAccountId: number;
  NumberOfTaxLots: number;
  TaxLotNumber: string;
  HoldingName: string;
  Symbol: string;
  CUSIP: string;
  SaleType: string | undefined;
  // Typed `| undefined` (not `| null`) so rows stay assignable to the grid export
  // type; the API can still omit these, so display sites guard with `!= null`.
  Quantity: number | undefined;
  OpenDate: string | undefined;
  TradeDate: string | undefined;
  SaleDate: string | undefined;
  CostBasis: number;
  SalePrice: number | undefined;
  CurrentBalance: number;
  DollarChange: number;
  PercentChange: number | undefined;
  TaxLotTermDisplay: string;
  GainTypeDisplay: string;
  AccountName: string;
  DiversificationName: string;
  // Index signature so rows satisfy MUI's `GridValidRowModel` and the export
  // button's `GridRowForExport` (both allow arbitrary extra keys).
  [key: string]: string | number | boolean | null | undefined;
}

// Row string fields are typed non-null, but the API can return null/undefined at
// runtime — trim safely so `.trim()` never throws.
const trimSafe = (value: string | null | undefined): string =>
  (value ?? '').trim();

const RealizedGainLossTab = ({
  startDate,
  endDate,
  holdingId,
  accountId,
  isWidget = false,
  investorId,
  groupBy = GainLossGroupByMode.NoGroup,
}: RealizedGainLossTabProps) => {
  const theme = useTheme();
  const authLoaded = useAuthIsLoaded();
  const apiRef = useGridApiRef();

  const { t } = useLocalization();
  const multipleLabel = t('gainsLosses.multiple');

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';
  const { tickerUrlFormatter } = useTickerUrlFormatter();
  // EUD-440: clicking an account name opens the Account Details flyout.
  const {
    openAccountById,
    openAccountByName,
    canOpenAccount,
    canOpenAccountByName,
    accountFlyout,
  } = useAccountDetailsFlyout({ investorId });
  const { openHolding, canOpenHolding, holdingFlyout } =
    useHoldingDetailsFlyout();
  const effectiveExclusions = useEffectiveExclusions();

  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
    Set<GridRowId>
  >(() => EMPTY_EXPANDED_ROW_IDS);

  const handleDetailPanelExpandedRowIdsChange = useCallback(
    (newIds: Set<GridRowId> | GridRowId[]) => {
      setDetailPanelExpandedRowIds(
        newIds instanceof Set ? newIds : new Set(newIds),
      );
    },
    [],
  );

  // Reset expansion when date or holdingId changes
  useEffect(() => {
    setDetailPanelExpandedRowIds(EMPTY_EXPANDED_ROW_IDS);
  }, [startDate, endDate, holdingId]);

  const { startDate: initialStartDate, endDate: initialEndDate } =
    getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);

  // Determine the query dates, using provided dates or defaults
  const queryStartDate = startDate || initialStartDate;
  const queryEndDate = endDate || initialEndDate;

  // Fetch grid columns configuration from API (matching legacy behavior)
  // CRITICAL BUG REPLICATION: Legacy UI has a bug where UnrealizedGainsGrid incorrectly
  // uses "investor-realized-gains" grid key in single holding view instead of
  // "investor-unrealized-gains". This causes both tabs to share the same column
  // configuration. We replicate this exact behavior for compatibility.
  const { data: gridColumnsData, isLoading: gridColumnsLoading } =
    useGridColumns(GRID_KEYS.INVESTOR_REALIZED_GAINS);

  const groupingModel = useMemo(
    () => getGainLossGroupingModel(groupBy),
    [groupBy],
  );

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    rowGroupingModel: groupingModel,
    initialState: {
      rowGrouping: { model: groupingModel },
      aggregation: {
        model: {
          CostBasis: 'sum',
          DollarChange: 'sum',
          PercentChange: 'realizedGainsPercentChangeAggregation',
        },
      },
      columns: {
        columnVisibilityModel: {
          GainTypeDisplay: false,
          AccountName: false,
        },
      },
    },
  });

  const initialStateWithMobileVisibility = useMemo(
    () => ({
      ...initialState,
      columns: {
        ...(initialState.columns ?? {}),
        columnVisibilityModel: {
          ...(initialState.columns?.columnVisibilityModel ?? {}),
          ...(isMobile
            ? Object.fromEntries(
                REALIZED_LIST_VIEW_OPTIONAL_FIELDS.map((field) => [
                  field,
                  false,
                ]),
              )
            : {}),
        },
      },
    }),
    [initialState, isMobile],
  );

  // Get realized gains data
  const {
    data: portfolioData,
    isFetching: portfolioFetching,
    error: portfolioError,
  } = useGetRealizedGainsQuery(
    authLoaded && !holdingId
      ? ({
          startDate: queryStartDate,
          endDate: queryEndDate,
          sort: [{ property: 'PercentChange', direction: 'desc', order: 1 }],
          AccountId: accountId,
          ...getGainLossFilterQueryParams(
            investorId,
            effectiveExclusions,
            groupBy,
          ),
        } satisfies PortfolioRealizedGainsQueryParams)
      : skipToken,
  );

  const {
    data: holdingData,
    isFetching: holdingFetching,
    error: holdingError,
  } = useGetHoldingRealizedGainsQuery(
    authLoaded && holdingId
      ? ({
          holdingId,
          startDate: queryStartDate,
          endDate: queryEndDate,
          sort: [{ property: 'PercentChange', direction: 'desc', order: 1 }],
          investorId,
        } satisfies RealizedGainsQueryParams)
      : skipToken,
  );

  // `useGetRealizedGainsQuery` (portfolio) is loosely typed (`any`) at this call
  // site, unlike the holding query; cast its results to the real shapes so the
  // records below are typed `RealizedGainsRecord` and the flags are booleans.
  type RealizedGainsResponse = RecordResponse<RealizedGainsRecord> | undefined;
  const typedPortfolioData = portfolioData as RealizedGainsResponse;
  const typedPortfolioFetching = portfolioFetching as boolean;
  const typedPortfolioError = portfolioError as typeof holdingError;
  const data: RealizedGainsResponse = holdingId
    ? holdingData
    : typedPortfolioData;
  const isLoading = holdingId ? holdingFetching : typedPortfolioFetching;
  const error = holdingId ? holdingError : typedPortfolioError;

  const rows: RealizedGainLossRow[] = data?.Records
    ? data.Records.map((record, index) => {
        const dateFields = getHoldingRowDateFields(record, holdingId);
        return {
          id: index, // Use index for unique id
          WealthHoldingId: record.WealthHoldingId,
          // EUD-440: carry the account id so the AccountName cell can open the flyout.
          WealthAccountId: record.WealthAccountId,
          NumberOfTaxLots: record.NumberOfTaxLots,
          TaxLotNumber: holdingId
            ? formatTaxLotNumber(record.TaxLotNumber)
            : '',
          HoldingName: record.HoldingName,
          Symbol: record.Symbol || '',
          CUSIP: record.CUSIP || '',
          SaleType: record.SaleType,
          Quantity: record.Quantity,
          OpenDate: dateFields.OpenDate,
          TradeDate: dateFields.TradeDate,
          SaleDate: dateFields.SaleDate,
          CostBasis: record.CostBasis,
          SalePrice: record.SalePrice,
          CurrentBalance: record.CurrentBalance, // Also include CurrentBalance for API field matching //! CurrentBalance does not exist in the data response.
          DollarChange: record.DollarChange,
          PercentChange: record.PercentChange,
          TaxLotTermDisplay: record.TaxLotTermDisplay,
          GainTypeDisplay: record.GainTypeDisplay,
          AccountName: record.AccountName,
          DiversificationName: record.DiversificationName ?? 'Unclassified',
        };
      })
    : [];

  // Create columns configuration based on API response
  const columns: GridColDef<RealizedGainLossRow>[] = useMemo(() => {
    // Default column definitions with full configuration
    // Note: Field names must match the GridColumnDataField from the API
    // Index signature is `| undefined`: lookups by a dynamic API field name may
    // miss, so callers must guard (kept honest for no-unnecessary-condition).
    const defaultColumns: Record<
      string,
      GridColDef<RealizedGainLossRow> | undefined
    > = {
      TaxLotNumber: {
        field: 'TaxLotNumber',
        headerName: t('grid.number'),
        flex: 1,
        getSortComparator: getTaxLotNumberSortComparator,
      },
      HoldingName: {
        field: 'HoldingName',
        headerName: t('grid.holding'),
        flex: 2,
        renderCell: (params: GridRenderCellParams<RealizedGainLossRow>) => {
          if (params.rowNode.type === 'group') {
            if (params.rowNode.groupingField !== 'HoldingName') return null;
            const key = params.rowNode.groupingKey;
            if (key == null) return null;
            return <Typography variant='body2'>{String(key)}</Typography>;
          }
          const cellRow = params.row as Partial<RealizedGainLossRow>;
          const name = trimSafe(cellRow.HoldingName);
          if (!name) return null;
          const holdingId = cellRow.WealthHoldingId;
          if (!canOpenHolding(holdingId)) {
            return <Typography variant='body2'>{name}</Typography>;
          }
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                cursor: 'pointer',
              }}
              onClick={() => {
                openHolding({
                  holdingId,
                  name,
                  accountName: cellRow.AccountName,
                });
              }}
            >
              <Typography
                variant='body2'
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {name}
              </Typography>
            </Box>
          );
        },
      },
      Symbol: {
        field: 'Symbol',
        headerName: t('grid.symbol'),
        width: 80,
        renderCell: (params) => {
          return tickerUrlFormatter(params.value);
        },
      },
      CUSIP: {
        field: 'CUSIP',
        headerName: t('grid.cusip'),
        flex: 1,
      },
      SaleType: {
        field: 'SaleType',
        headerName: t('grid.type'),
        flex: 1,
        valueGetter: (value: string, row) => {
          if (isAutogeneratedRow(row) || row.NumberOfTaxLots > 1 || !value) {
            return '';
          }

          return value
            .split(' ')
            .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
            .join(' ');
        },
      },
      Quantity: {
        field: 'Quantity',
        headerName: t('grid.quantity'),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params: number) => {
          return params
            ? params.toLocaleString('en-US', { maximumFractionDigits: 20 })
            : '';
        },
      },
      OpenDate: GainsLossesUtility.createDateColumn({
        field: 'OpenDate',
        headerName: t('grid.openDate'),
        multipleLabel,
      }) as GridColDef<RealizedGainLossRow>,
      TradeDate: GainsLossesUtility.createDateColumn({
        field: 'TradeDate',
        headerName: t('grid.tradeDate'),
        multipleLabel,
      }) as GridColDef<RealizedGainLossRow>,
      SaleDate: GainsLossesUtility.createDateColumn({
        field: 'SaleDate',
        headerName: t('grid.settlementDate'),
        multipleLabel,
      }) as GridColDef<RealizedGainLossRow>,
      CostBasis: {
        field: 'CostBasis',
        headerName: t('grid.costBasis'),
        type: 'number',
        width: GRID_COLUMN_WIDTH_NARROW,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params: number) => formatNumberToCurrency(params),
      },
      SalePrice: {
        field: 'SalePrice',
        headerName: t('grid.salePrice'),
        type: 'number',
        width: GRID_COLUMN_WIDTH_NARROW,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value: number | undefined, row) => {
          if (!value || isAutogeneratedRow(row) || row.NumberOfTaxLots > 1) {
            return '';
          }

          return formatNumberToCurrency(value);
        },
      },
      //? I'm not sure what this does, because the data doesn't exist.
      CurrentBalance: {
        field: 'CurrentBalance',
        headerName: t('grid.salePrice'), // Override to "Sale Price" for realized gains
        type: 'number',
        width: GRID_COLUMN_WIDTH_NARROW,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params: number) => formatNumberToCurrency(params),
      },
      DollarChange: {
        field: 'DollarChange',
        headerName: t('grid.dollarChange'),
        type: 'number',
        width: GRID_COLUMN_WIDTH_NARROW,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params) => formatNumberToCurrency(params),
        cellClassName: (params: GridCellParams) => {
          return (params.value as number) >= 0
            ? 'positive-value'
            : 'negative-value';
        },
      },
      PercentChange: {
        field: 'PercentChange',
        headerName: t('grid.percentChange'),
        type: 'number',
        width: GRID_COLUMN_WIDTH_NARROW,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params: number) => formatPercentValue(params),
        cellClassName: (params: GridCellParams) => {
          return (params.value as number) >= 0
            ? 'positive-value'
            : 'negative-value';
        },
      },
      TaxLotTermDisplay: {
        field: 'TaxLotTermDisplay',
        headerName: t('grid.term'),
        flex: 1,
      },
      // Add account column for grouping purposes
      AccountName: {
        field: 'AccountName',
        headerName: t('grid.account'),
        width: GRID_COLUMN_WIDTH_NARROW,
        // EUD-440: clicking the account name opens the Account Details flyout.
        renderCell: (params: GridRenderCellParams<RealizedGainLossRow>) => {
          // When grouping by account, the grid delegates the group-header value to
          // this renderCell; group nodes have no AccountName field, so render the
          // grouping key (the account name) and open the flyout by name.
          if (params.rowNode.type === 'group') {
            // This is the AccountName column; it only has a value for account
            // groups. For any other grouping field the group row's cell here is
            // empty so it doesn't echo the other group's key (EUD-440).
            if (params.rowNode.groupingField !== 'AccountName') {
              return null;
            }
            const key = params.rowNode.groupingKey;
            if (key == null) return null;
            const groupName = String(key);
            if (!canOpenAccountByName(groupName)) {
              return <Typography variant='body2'>{groupName}</Typography>;
            }
            return (
              <Typography
                variant='body2'
                onClick={(event) => {
                  event.stopPropagation();
                  openAccountByName(groupName);
                }}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                {groupName}
              </Typography>
            );
          }
          const name = params.row.AccountName;
          if (!name) return null;
          const accountId = params.row.WealthAccountId;
          if (!canOpenAccount(accountId)) {
            return <Typography variant='body2'>{name}</Typography>;
          }
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                cursor: 'pointer',
              }}
              onClick={() => {
                openAccountById(accountId);
              }}
            >
              <Typography
                variant='body2'
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {name}
              </Typography>
            </Box>
          );
        },
      },
      // Add gainOrLoss column for grouping purposes
      GainOrLoss: {
        field: 'GainTypeDisplay',
        headerName: t('grid.gainOrLoss'),
        width: 120,
        valueGetter: (_value, row) => {
          if (isAutogeneratedRow(row)) {
            return '';
          }

          const dollarChange = row.DollarChange as number | null | undefined;
          if (typeof dollarChange !== 'number' || Number.isNaN(dollarChange)) {
            return '';
          }

          return dollarChange >= 0 ? t('gain') : t('loss');
        },
      },
      // Add DiversificationName column for asset allocation grouping
      DiversificationName: {
        field: 'DiversificationName',
        headerName: t('grid.assetAllocation', 'Asset Allocation'),
        width: GRID_COLUMN_WIDTH_NARROW,
        align: 'left',
        headerAlign: 'left',
      },
    };

    // If we have grid columns configuration from the API, use it
    if (gridColumnsData?.Records && gridColumnsData.Records.length > 0) {
      const enabledColumns = gridColumnsData.Records.filter(
        (col) => col.IsEnabled,
      )
        .sort((a, b) => a.Order - b.Order)
        .map((col) => {
          const baseColumn = defaultColumns[col.GridColumnDataField];
          if (baseColumn) {
            return {
              ...baseColumn,
              headerName: col.Label, // Use the label from API
            };
          }

          // Create a dynamic column for unknown fields
          console.warn('Creating dynamic column for:', col.GridColumnDataField);
          return {
            field: col.GridColumnDataField,
            headerName: col.Label,
            width: 120,
            align: 'left' as const,
            headerAlign: 'left' as const,
            sortable: true,
          } as GridColDef<RealizedGainLossRow>;
        })
        .filter(Boolean);

      // Always ensure required grouping columns are available (even if not explicitly configured)
      const requiredGroupingColumns = [
        defaultColumns.AccountName,
        defaultColumns.GainOrLoss,
      ].filter(
        (col): col is GridColDef<RealizedGainLossRow> =>
          col != null &&
          !enabledColumns.some((enabledCol) => enabledCol.field === col.field),
      );

      // Legacy parity: ensure Open Date is available by default.
      // The backend returns `OpenDate`, and legacy UI displayed it even when column config omitted it.
      const requiredDisplayColumns = [defaultColumns.OpenDate].filter(
        (col): col is GridColDef<RealizedGainLossRow> =>
          col != null &&
          !enabledColumns.some((enabledCol) => enabledCol.field === col.field),
      );

      // Only add DiversificationName column when grouping by asset allocation
      const classificationColumn =
        groupBy === GainLossGroupByMode.ByAssetAllocation &&
        defaultColumns.DiversificationName
          ? [defaultColumns.DiversificationName]
          : [];

      const columnsWithGrouping: GridColDef<RealizedGainLossRow>[] = [
        ...enabledColumns,
        ...requiredGroupingColumns,
        ...requiredDisplayColumns,
        ...classificationColumn,
      ];

      // Special handling for single holding view - matches legacy behavior
      if (holdingId) {
        // Remove HoldingName and Symbol columns and add TaxLotNumber at the beginning
        const filteredColumns = columnsWithGrouping.filter(
          (col) => col.field !== 'HoldingName' && col.field !== 'Symbol',
        );

        const findField = (field: string) =>
          filteredColumns.find((col) => col.field === field);
        const currentBalanceColumn = findField('CurrentBalance');
        const salePriceColumn = findField('SalePrice');
        const openDateColumn = findField('OpenDate');

        // For single holding view, reorder columns to match legacy UI exactly:
        // #, Type, Quantity, Trade Date, Settlement Date, Cost Basis, Sale Price, $ Change, % Change, Term, Open Date
        const reorderedColumns = [
          defaultColumns.TaxLotNumber,
          // Type column - add if not present in API
          findField('SaleType') || defaultColumns.SaleType,
          findField('Quantity'),
          findField('TradeDate') || {
            // Fallback to OpenDate with "Trade Date" label
            ...defaultColumns.OpenDate,
            field: 'TradeDate',
            headerName: 'Trade Date',
          },
          findField('SaleDate') || {
            // Fallback to OpenDate with "Settlement Date" label
            ...defaultColumns.OpenDate,
            field: 'SaleDate',
            headerName: 'Settlement Date',
          },
          findField('CostBasis'),
          // Override label for Sale Price (CurrentBalance field maps to Sale Price for realized gains)
          currentBalanceColumn
            ? {
                ...currentBalanceColumn,
                headerName: 'Sale Price',
              }
            : salePriceColumn
              ? {
                  ...salePriceColumn,
                  headerName: 'Sale Price',
                }
              : defaultColumns.CurrentBalance,
          findField('DollarChange'),
          findField('PercentChange'),
          findField('TaxLotTermDisplay'),
          // Override label for Open Date
          openDateColumn
            ? {
                ...openDateColumn,
                headerName: 'Open Date',
              }
            : defaultColumns.OpenDate,
        ].filter((col): col is GridColDef<RealizedGainLossRow> => col != null);

        return reorderedColumns;
      }

      return columnsWithGrouping;
    }

    // Fallback: return default enabled columns matching legacy UI realized gains
    const baseColumns = [
      defaultColumns.HoldingName,
      defaultColumns.Symbol,
      defaultColumns.AccountName,
      defaultColumns.SaleType,
      defaultColumns.Quantity,
      defaultColumns.OpenDate,
      defaultColumns.TradeDate,
      defaultColumns.SaleDate,
      defaultColumns.CostBasis,
      defaultColumns.SalePrice,
      defaultColumns.DollarChange,
      defaultColumns.PercentChange,
      defaultColumns.TaxLotTermDisplay,
      defaultColumns.GainOrLoss,
      // Only include DiversificationName column when grouping by asset allocation
      ...(groupBy === GainLossGroupByMode.ByAssetAllocation
        ? [defaultColumns.DiversificationName]
        : []),
    ].filter(Boolean) as GridColDef<RealizedGainLossRow>[];

    // Special handling for single holding view in fallback
    if (holdingId) {
      // For single holding view, use specific columns in the correct order
      // Based on legacy UI: #, Type, Quantity, Trade Date, Settlement Date, Cost Basis, Sale Price, $ Change, % Change, Term, Open Date
      return [
        defaultColumns.TaxLotNumber,
        defaultColumns.AccountName,
        defaultColumns.SaleType,
        defaultColumns.Quantity,
        defaultColumns.TradeDate,
        defaultColumns.SaleDate,
        defaultColumns.CostBasis,
        defaultColumns.SalePrice,
        defaultColumns.DollarChange,
        defaultColumns.PercentChange,
        defaultColumns.TaxLotTermDisplay,
        defaultColumns.OpenDate,
        defaultColumns.GainOrLoss,
        // Only include DiversificationName column when grouping by asset allocation
        ...(groupBy === GainLossGroupByMode.ByAssetAllocation
          ? [defaultColumns.DiversificationName]
          : []),
      ].filter(Boolean) as GridColDef<RealizedGainLossRow>[];
    }

    return baseColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gridColumnsData?.Records,
    holdingId,
    t,
    groupBy,
    // Accounts load after first render; without these the desktop columns keep
    // the initial (empty-map) callbacks and render account names as plain text
    // even after the lookup resolves (EUD-440 review).
    canOpenAccount,
    openAccountById,
    canOpenAccountByName,
    openAccountByName,
    canOpenHolding,
    openHolding,
  ]);

  // Defensive: DataGrid internals assume every entry is a valid column object.
  // The column builder above already narrows out nullish entries (via typed
  // `.filter(...)`), so `columns` is guaranteed to contain only real column
  // objects with a string `field`; re-expose under the original name.
  const safeColumns: GridColDef<RealizedGainLossRow>[] = columns;

  const listViewColDef: GridListViewColDef<RealizedGainLossRow> = useMemo(
    () => ({
      field: 'listColumn',
      headerName: '',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<RealizedGainLossRow>) => {
        const { rowNode, api, row } = params;

        const visibilityModel = api.state.columns.columnVisibilityModel;

        const isFieldVisible = (field: string) =>
          // A field absent from the visibility model is visible; only an explicit
          // `false` hides it. The index access is typed `boolean` but may be
          // `undefined` at runtime, so read it through an honest `| undefined`.
          (visibilityModel[field] as boolean | undefined) !== false;

        if (rowNode.type === 'group') {
          const isExpanded = !!rowNode.childrenExpanded;
          const groupKey = String(rowNode.groupingKey ?? params.value ?? '');

          const apiPremium = api as GridApiPremium;
          const descendantCount = (
            rowNode as GridGroupNode & { descendantCount?: number }
          ).descendantCount;
          const childrenIdsCount = (
            rowNode as GridGroupNode & { childrenIds?: (string | number)[] }
          ).childrenIds?.length;
          const apiChildrenCount =
            typeof apiPremium.getRowGroupChildren === 'function'
              ? apiPremium.getRowGroupChildren({
                  groupId: params.id,
                  applySorting: true,
                  applyFiltering: true,
                }).length
              : undefined;
          const groupCount =
            descendantCount ?? childrenIdsCount ?? apiChildrenCount ?? 0;

          const handleToggleExpand = (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            (api as GridApiPremium).setRowChildrenExpansion(
              params.id,
              !isExpanded,
            );
          };

          return (
            <Box
              sx={{
                width: '100%',
                px: 0,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <IconButton
                size='small'
                onClick={handleToggleExpand}
                sx={{ p: 0 }}
                aria-label={
                  isExpanded
                    ? t('grid.collapseGroup', 'Collapse group')
                    : t('grid.expandGroup', 'Expand group')
                }
              >
                {isExpanded ? (
                  <ExpandMoreIcon fontSize='small' />
                ) : (
                  <ChevronRightIcon fontSize='small' />
                )}
              </IconButton>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: '600 !important',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {rowNode.groupingField === 'AccountName' &&
                canOpenAccountByName(groupKey) ? (
                  <Box
                    component='span'
                    onClick={(event) => {
                      event.stopPropagation();
                      openAccountByName(groupKey);
                    }}
                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {groupKey}
                  </Box>
                ) : (
                  groupKey
                )}
                {groupCount > 0 ? ` (${String(groupCount)})` : ''}
              </Typography>
            </Box>
          );
        }

        const holdingName = trimSafe(row.HoldingName);
        const accountName = trimSafe(row.AccountName);
        const accountId = row.WealthAccountId;
        // EUD-658: four-digit year on the mobile card to match Realized Gains.
        const openDateValue = row.OpenDate ? formatAppDate(row.OpenDate) : '';
        const tradeDateValue = row.TradeDate
          ? formatAppDate(row.TradeDate)
          : '';
        const saleDateValue = row.SaleDate ? formatAppDate(row.SaleDate) : '';

        const term = trimSafe(row.TaxLotTermDisplay);
        const taxLotNumber = trimSafe(row.TaxLotNumber);
        const symbol = trimSafe(row.Symbol);
        const cusip = trimSafe(row.CUSIP);
        const saleType = (row.SaleType ?? '').trim();
        const classificationName = trimSafe(row.DiversificationName);
        const quantity = row.Quantity;
        // Match the desktop "Sale Price" column, which prefers CurrentBalance
        // (relabeled) and falls back to SalePrice. CurrentBalance is typed
        // non-null but can be absent at runtime (it isn't in the API response),
        // so read it null-honestly for the fallback to actually apply (EUD-440).
        const salePrice =
          (row.CurrentBalance as number | undefined) ?? row.SalePrice;
        // CostBasis/DollarChange are typed non-null but the API can omit them for
        // unavailable tax lots; read null-honestly so a missing value renders '—'
        // instead of a misleading $0.00 / non-negative color (EUD-440 review).
        const costBasis = row.CostBasis as number | null | undefined;
        const dollarChange = row.DollarChange as number | null | undefined;
        const percentChange = row.PercentChange;

        const getChangeColor = (value: number) =>
          value >= 0 ? theme.palette.success.main : theme.palette.error.main;

        const Line = (line: {
          left: string;
          right?: string;
          rightColor?: string;
        }) => (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography
              variant='body2'
              sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }}
            >
              {line.left}
            </Typography>
            {line.right != null ? (
              <Typography
                variant='body2'
                sx={{
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  textAlign: 'right',
                  color: line.rightColor ?? 'text.secondary',
                }}
              >
                {line.right}
              </Typography>
            ) : null}
          </Box>
        );

        const showHolding = isFieldVisible('HoldingName');
        const showSalePrice =
          isFieldVisible('CurrentBalance') || isFieldVisible('SalePrice');
        const showOpenDate = isFieldVisible('OpenDate');
        const showTradeDate = isFieldVisible('TradeDate');
        const showSaleDate = isFieldVisible('SaleDate');
        const showTerm = isFieldVisible('TaxLotTermDisplay');
        const showTaxLotNumber = isFieldVisible('TaxLotNumber');
        const showSymbol = isFieldVisible('Symbol');
        const showCUSIP = isFieldVisible('CUSIP');
        const showSaleType = isFieldVisible('SaleType');
        const showClassificationName = isFieldVisible('DiversificationName');
        const showQuantity = isFieldVisible('Quantity');
        const showCostBasis = isFieldVisible('CostBasis');
        const showDollarChange = isFieldVisible('DollarChange');
        const showPercentChange = isFieldVisible('PercentChange');
        const showAccount = isFieldVisible('AccountName');

        return (
          <Box
            sx={{
              width: '100%',
              px: 0,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {(showHolding || showSalePrice) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography
                  variant='body1'
                  onClick={
                    showHolding && canOpenHolding(row.WealthHoldingId)
                      ? (event) => {
                          event.stopPropagation();
                          openHolding({
                            holdingId: row.WealthHoldingId,
                            name: holdingName,
                            accountName: row.AccountName,
                          });
                        }
                      : undefined
                  }
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                    fontWeight: '600 !important',
                    color: 'text.primary',
                    ...(showHolding && canOpenHolding(row.WealthHoldingId)
                      ? { cursor: 'pointer', textDecoration: 'underline' }
                      : {}),
                  }}
                >
                  {showHolding ? holdingName : t('grid.holding', 'Holding')}
                </Typography>
                {showSalePrice ? (
                  <Typography
                    variant='body1'
                    sx={{
                      fontWeight: '600 !important',
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                      textAlign: 'right',
                    }}
                  >
                    {formatNumberToCurrency(salePrice)}
                  </Typography>
                ) : null}
              </Box>
            )}

            {showAccount && accountName ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                {/* EUD-440: tapping the account name opens the Account Details flyout. */}
                <Typography
                  variant='body1'
                  onClick={
                    canOpenAccount(accountId)
                      ? (event) => {
                          event.stopPropagation();
                          openAccountById(accountId);
                        }
                      : undefined
                  }
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: 'text.secondary',
                    ...(canOpenAccount(accountId)
                      ? { cursor: 'pointer', textDecoration: 'underline' }
                      : {}),
                  }}
                >
                  {accountName}
                </Typography>
              </Box>
            ) : null}

            {(showTerm || showQuantity) && (term || quantity != null) ? (
              <Line
                left={
                  showTerm && term
                    ? t('grid.termLabel', {
                        term,
                        defaultValue: `${term}-term`,
                      })
                    : t('grid.term', 'Term')
                }
                right={
                  showQuantity && quantity != null
                    ? quantity.toLocaleString('en-US', {
                        maximumFractionDigits: 20,
                      })
                    : undefined
                }
              />
            ) : null}

            {showCostBasis ? (
              <Line
                left={t('grid.costBasis', 'Cost Basis')}
                right={
                  typeof costBasis === 'number'
                    ? formatNumberToCurrency(costBasis)
                    : '—'
                }
              />
            ) : null}

            {showDollarChange ? (
              <Line
                left={t('grid.dollarChange', '$ Change')}
                right={
                  typeof dollarChange === 'number'
                    ? formatNumberToCurrency(dollarChange)
                    : '—'
                }
                rightColor={
                  typeof dollarChange === 'number'
                    ? getChangeColor(dollarChange)
                    : undefined
                }
              />
            ) : null}

            {showPercentChange && percentChange != null ? (
              <Line
                left={t('grid.percentChange', '% Change')}
                right={`${percentChange.toFixed(2)}%`}
                rightColor={getChangeColor(percentChange)}
              />
            ) : null}

            {showOpenDate && openDateValue ? (
              <Line
                left={t('grid.openDate', 'Open Date')}
                right={openDateValue}
              />
            ) : null}

            {showTradeDate && tradeDateValue ? (
              <Line
                left={t('grid.tradeDate', 'Trade Date')}
                right={tradeDateValue}
              />
            ) : null}

            {showSaleDate && saleDateValue ? (
              <Line
                left={t('grid.settlementDate', 'Settlement Date')}
                right={saleDateValue}
              />
            ) : null}

            {showTaxLotNumber && taxLotNumber ? (
              <Line
                left={t('grid.number', 'Number')}
                right={taxLotNumber}
              />
            ) : null}

            {showSaleType && saleType ? (
              <Line
                left={t('grid.type', 'Type')}
                right={saleType}
              />
            ) : null}

            {showSymbol && symbol ? (
              <Line
                left={t('grid.symbol', 'Symbol')}
                right={symbol}
              />
            ) : null}

            {showCUSIP && cusip ? (
              <Line
                left={t('grid.cusip', 'CUSIP')}
                right={cusip}
              />
            ) : null}

            {showClassificationName && classificationName ? (
              <Line
                left={t('grid.assetAllocation', 'Asset Allocation')}
                right={classificationName}
              />
            ) : null}
          </Box>
        );
      },
    }),
    [
      t,
      theme.palette.error.main,
      theme.palette.success.main,
      canOpenAccount,
      openAccountById,
      canOpenAccountByName,
      openAccountByName,
      canOpenHolding,
      openHolding,
    ],
  );

  // Hide "Detail panel toggle"
  // https://github.com/mui/mui-x/issues/9968
  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => column.field !== GRID_DETAIL_PANEL_TOGGLE_FIELD)
      .map((column) => column.field);
  };

  // Hide export in the Holding Details flyout (holdingId set): its rows are
  // already scoped "By Tax Lots" for that holding, and export always hits the
  // portfolio-wide endpoint, so it would download the wrong data.
  const canExport = !isNative && !isMobile && !holdingId;

  return (
    <Stack
      direction='column'
      gap={3}
    >
      <WidgetHeader
        title={
          holdingId
            ? t('byTaxLots')
            : `${t('byHoldings')} (${formatAppDate(queryStartDate)} - ${formatAppDate(queryEndDate)})`
        }
      />

      {error ? (
        <Typography color='error'>
          Error loading data: {JSON.stringify(error)}
        </Typography>
      ) : (
        <Box
          sx={{
            width: '100%',
          }}
        >
          {canExport && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <ExportButton
                columns={withTaxLotNumberColumn(
                  safeColumns,
                  t('grid.lotNumber'),
                )}
                disabled={isLoading}
                label={t('grid.exportWithTaxLots')}
                fileName='RealizedGainsWithTaxLots'
                childProperty='TaxLots'
                dataUrl='realizedGains'
                investorId={investorId}
                queryParams={{
                  StartDate: queryStartDate,
                  EndDate: queryEndDate,
                  ...getGainLossFilterQueryParams(
                    investorId,
                    effectiveExclusions,
                    groupBy,
                  ),
                  sort: [
                    {
                      Property: 'PercentChange',
                      Direction: 'desc',
                      Order: 1,
                    },
                  ],
                }}
              />
            </Box>
          )}

          <GridContainer isWidget={isWidget}>
            <DataGridPremium
              key={
                isMobile
                  ? `realized-gains-xs-${holdingId != null ? String(holdingId) : 'portfolio'}-${groupBy}`
                  : `realized-gains-${holdingId != null ? String(holdingId) : 'portfolio'}-${groupBy}`
              }
              rows={rows}
              columns={safeColumns}
              apiRef={apiRef}
              rowGroupingModel={groupingModel}
              initialState={initialStateWithMobileVisibility}
              aggregationFunctions={{
                ...GRID_AGGREGATION_FUNCTIONS,
                realizedGainsPercentChangeAggregation, // custom functions MUST be added here.
              }}
              defaultGroupingExpansionDepth={-1}
              disableRowSelectionOnClick
              disableColumnFilter
              showToolbar
              disablePivoting
              listView={isMobile}
              {...(isMobile ? { listViewColumn: listViewColDef } : {})}
              {...(isMobile ? { getRowHeight: () => 'auto' as const } : {})}
              getDetailPanelContent={({ row }) => {
                if (!shouldShowTaxLotDetailPanel(row, holdingId)) return null;
                return (
                  <TaxLotsDetailGrid
                    realized={true}
                    holdingId={row.WealthHoldingId}
                    investorId={investorId}
                    startDate={queryStartDate}
                    endDate={queryEndDate}
                    parentColumns={safeColumns}
                  />
                );
              }}
              getDetailPanelHeight={() => 'auto'}
              detailPanelExpandedRowIds={detailPanelExpandedRowIds}
              onDetailPanelExpandedRowIdsChange={
                handleDetailPanelExpandedRowIdsChange
              }
              loading={isLoading || !authLoaded || gridColumnsLoading}
              slotProps={{
                loadingOverlay: {
                  variant: 'linear-progress',
                  noRowsVariant: 'skeleton',
                },
                toolbar: {
                  ...defaultToolbarExportSlotProps,
                  showQuickFilter: true,
                },
                columnsManagement: {
                  getTogglableColumns,
                },
              }}
              slots={{
                ...(isNative
                  ? {
                      toolbar: (
                        props: Parameters<typeof CustomToolbarForNative>[0],
                      ) => <CustomToolbarForNative {...props} />,
                    }
                  : isMobile
                    ? {
                        toolbar: (
                          props: Parameters<
                            typeof XsPreferencesDrawerToolbar
                          >[0],
                        ) => (
                          <XsPreferencesDrawerToolbar
                            {...props}
                            showFilters={false}
                          />
                        ),
                      }
                    : {}),
              }}
              sx={{
                '& .positive-value': {
                  color: theme.palette.success.main,
                  fontWeight: 600,
                },
                '& .negative-value': {
                  color: theme.palette.error.main,
                  fontWeight: 600,
                },
                // Hide aggregation label
                '& .MuiDataGrid-aggregationColumnHeaderLabel': {
                  display: 'none',
                },
              }}
            />
          </GridContainer>
        </Box>
      )}

      {accountFlyout}
      {holdingFlyout}
    </Stack>
  );
};

export default RealizedGainLossTab;
